import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  institutionPFiles,
  institutionUsers,
  type PFile,
  type PFileMovement,
  type PFileRequest,
  type PFileStats,
  type User,
  type UnassignedDocument,
} from '@/services/institution';
import { Modal } from '@/components/common/Modal';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { Select } from '@/components/common/Select';
import { SmartSearch } from '@/components/ai/SmartSearch';
import { DocumentScanner } from '@/components/ai/DocumentScanner';
import { anomalyDetectionService, type AnomalyReport } from '@/services/ai';
import { type ParsedQuery } from '@/services/ai';
import { retentionEngine, type RetentionSchedule } from '@/services/compliance/retentionEngine';
import styles from './PFilesPage.module.css';

export function PFilesPage() {
  const [pfiles, setPFiles] = useState<PFile[]>([]);
  const [staffMembers, setStaffMembers] = useState<User[]>([]);
  const [stats, setStats] = useState<PFileStats | null>(null);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCheckedOut, setFilterCheckedOut] = useState('');

  // Create PFile modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    staffId: '',
    physicalLocation: '',
    building: '',
    room: '',
    cabinet: '',
    shelf: '',
    notes: '',
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [unassignedDocs, setUnassignedDocs] = useState<UnassignedDocument[]>([]);
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);

  // Detail sidebar
  const [selectedPFile, setSelectedPFile] = useState<PFile | null>(null);
  const [showDetailSidebar, setShowDetailSidebar] = useState(false);
  const [movements, setMovements] = useState<PFileMovement[]>([]);
  const [loadingMovements, setLoadingMovements] = useState(false);

  // Checkout modal
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [checkoutReason, setCheckoutReason] = useState('');
  const [expectedReturnDate, setExpectedReturnDate] = useState('');
  const [checkingOut, setCheckingOut] = useState(false);

  // Checkin modal
  const [showCheckinModal, setShowCheckinModal] = useState(false);
  const [checkinNotes, setCheckinNotes] = useState('');
  const [checkinLocation, setCheckinLocation] = useState('');
  const [checkingIn, setCheckingIn] = useState(false);

  // Transfer modal
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferToUserId, setTransferToUserId] = useState('');
  const [transferNotes, setTransferNotes] = useState('');
  const [transferring, setTransferring] = useState(false);

  // Request modal
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestPurpose, setRequestPurpose] = useState('');
  const [requestUrgency, setRequestUrgency] = useState('normal');
  const [requestNeededBy, setRequestNeededBy] = useState('');
  const [requesting, setRequesting] = useState(false);

  // Pending requests
  const [pendingRequests, setPendingRequests] = useState<PFileRequest[]>([]);
  const [showRequestsModal, setShowRequestsModal] = useState(false);

  // AI Features
  const [showSmartSearch, setShowSmartSearch] = useState(false);
  const [showDocScanner, setShowDocScanner] = useState(false);
  const [anomalyReports, setAnomalyReports] = useState<Map<string, AnomalyReport>>(new Map());
  const [showAnomalyPanel, setShowAnomalyPanel] = useState(false);
  const [selectedAnomalyReport, setSelectedAnomalyReport] = useState<AnomalyReport | null>(null);

  // Compliance Integration
  const [retentionSchedule, setRetentionSchedule] = useState<RetentionSchedule | null>(null);

  // Fetch PFiles
  const fetchPFiles = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | boolean | undefined> = {};
      if (searchQuery) params.search = searchQuery;
      if (filterStatus) params.status = filterStatus;
      if (filterCheckedOut) params.isCheckedOut = filterCheckedOut === 'true';

      const response = await institutionPFiles.list(params);
      if (response.success && response.data) {
        setPFiles(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch PFiles:', err);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filterStatus, filterCheckedOut]);

  // Fetch reference data
  const fetchReferenceData = useCallback(async () => {
    try {
      const [usersRes, statsRes] = await Promise.all([
        institutionUsers.list({ perPage: 100 }),
        institutionPFiles.getStats(),
      ]);

      if (usersRes.success && usersRes.data) {
        setStaffMembers(usersRes.data);
      }
      if (statsRes.success && statsRes.data) {
        setStats(statsRes.data);
      }
    } catch (err) {
      console.error('Failed to fetch reference data:', err);
    }
  }, []);

  // Fetch pending requests
  const fetchPendingRequests = useCallback(async () => {
    try {
      const response = await institutionPFiles.getPendingRequests();
      if (response.success && response.data) {
        setPendingRequests(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch pending requests:', err);
    }
  }, []);

  // Fetch unassigned documents for a staff member
  const fetchUnassignedDocs = useCallback(async (staffId: string) => {
    setLoadingDocs(true);
    try {
      const response = await institutionPFiles.getUnassignedDocuments(staffId);
      if (response.success && response.data) {
        setUnassignedDocs(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch unassigned documents:', err);
    } finally {
      setLoadingDocs(false);
    }
  }, []);

  useEffect(() => {
    fetchReferenceData();
    fetchPendingRequests();
  }, [fetchReferenceData, fetchPendingRequests]);

  useEffect(() => {
    fetchPFiles();
  }, [fetchPFiles]);

  // Run anomaly detection on PFiles
  useEffect(() => {
    if (pfiles.length > 0) {
      const reports = new Map<string, AnomalyReport>();
      pfiles.forEach(pfile => {
        // Convert PFile to the format expected by anomaly detection
        const recordData = {
          id: pfile.id,
          staffName: `${pfile.staff.firstName} ${pfile.staff.lastName}`,
          fileNumber: pfile.fileNumber,
          staffId: pfile.staff.employeeId || undefined,
          department: pfile.staff.department?.name,
          status: pfile.status,
          isCheckedOut: pfile.isCheckedOut,
          checkedOutAt: pfile.checkedOutAt || undefined,
          checkedOutTo: pfile.checkedOutToUser ? `${pfile.checkedOutToUser.firstName} ${pfile.checkedOutToUser.lastName}` : undefined,
          documents: [], // Would need to fetch documents for full analysis
          jackets: [], // Would need to fetch jackets for full analysis
          createdAt: pfile.createdAt,
          updatedAt: pfile.updatedAt,
        };
        const report = anomalyDetectionService.analyzePFile(recordData);
        if (report.anomalies.length > 0 || report.completenessScore < 100) {
          reports.set(pfile.id, report);
        }
      });
      setAnomalyReports(reports);
    }
  }, [pfiles]);

  // Get flagged PFiles (with anomalies or low completeness)
  const flaggedPFiles = useMemo(() => {
    return pfiles.filter(pf => {
      const report = anomalyReports.get(pf.id);
      return report && (report.riskLevel === 'critical' || report.riskLevel === 'high');
    });
  }, [pfiles, anomalyReports]);

  // Handle smart search
  const handleSmartSearch = useCallback((parsed: ParsedQuery) => {
    // Apply parsed filters to the search
    const searchTerms: string[] = [];

    parsed.filters.forEach(filter => {
      if (filter.field === 'isCheckedOut') {
        setFilterCheckedOut(filter.value ? 'true' : 'false');
      } else if (filter.field === 'status') {
        setFilterStatus(String(filter.value));
      } else if (filter.displayValue) {
        searchTerms.push(filter.displayValue);
      }
    });

    if (searchTerms.length > 0 || parsed.originalQuery) {
      setSearchQuery(parsed.originalQuery);
    }

    setShowSmartSearch(false);
  }, []);

  // Handle staff selection change - fetch unassigned documents
  function handleStaffChange(staffId: string) {
    setCreateForm({ ...createForm, staffId });
    setSelectedDocIds([]);
    if (staffId) {
      fetchUnassignedDocs(staffId);
    } else {
      setUnassignedDocs([]);
    }
  }

  // Toggle document selection
  function toggleDocSelection(docId: string) {
    setSelectedDocIds((prev) =>
      prev.includes(docId) ? prev.filter((id) => id !== docId) : [...prev, docId]
    );
  }

  // Create PFile
  async function handleCreatePFile(e: React.FormEvent) {
    e.preventDefault();
    if (!createForm.staffId) {
      setCreateError('Please select a staff member');
      return;
    }

    setCreating(true);
    setCreateError(null);

    try {
      const response = await institutionPFiles.create({
        staffId: createForm.staffId,
        physicalLocation: createForm.physicalLocation || undefined,
        building: createForm.building || undefined,
        room: createForm.room || undefined,
        cabinet: createForm.cabinet || undefined,
        shelf: createForm.shelf || undefined,
        notes: createForm.notes || undefined,
        documentIds: selectedDocIds.length > 0 ? selectedDocIds : undefined,
      });

      if (response.success) {
        setShowCreateModal(false);
        setCreateForm({ staffId: '', physicalLocation: '', building: '', room: '', cabinet: '', shelf: '', notes: '' });
        setSelectedDocIds([]);
        setUnassignedDocs([]);
        fetchPFiles();
        fetchReferenceData();
      } else {
        setCreateError(response.error?.message || 'Failed to create PFile');
      }
    } catch (err) {
      setCreateError('An error occurred');
    } finally {
      setCreating(false);
    }
  }

  // Open detail sidebar
  async function openDetailSidebar(pfile: PFile) {
    setSelectedPFile(pfile);
    setShowDetailSidebar(true);
    setLoadingMovements(true);

    try {
      const response = await institutionPFiles.getMovements(pfile.id);
      if (response.success && response.data) {
        setMovements(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch movements:', err);
    } finally {
      setLoadingMovements(false);
    }

    // Calculate retention schedule
    const retention = retentionEngine.calculateRetentionSchedule({
      pfileId: pfile.id,
      staffName: `${pfile.staff.firstName} ${pfile.staff.lastName}`,
      fileNumber: pfile.fileNumber,
      recordType: 'personnel_file',
      createdAt: new Date(pfile.createdAt),
      isOnLegalHold: false,
    });
    setRetentionSchedule(retention);
  }

  function closeDetailSidebar() {
    setShowDetailSidebar(false);
    setSelectedPFile(null);
    setMovements([]);
    setRetentionSchedule(null);
  }

  // Checkout
  async function handleCheckout() {
    if (!selectedPFile || !checkoutReason) return;

    setCheckingOut(true);
    try {
      const response = await institutionPFiles.checkout(
        selectedPFile.id,
        checkoutReason,
        expectedReturnDate || undefined
      );

      if (response.success) {
        setShowCheckoutModal(false);
        setCheckoutReason('');
        setExpectedReturnDate('');
        fetchPFiles();
        if (response.data) {
          setSelectedPFile(response.data);
        }
      }
    } catch (err) {
      console.error('Checkout failed:', err);
    } finally {
      setCheckingOut(false);
    }
  }

  // Checkin
  async function handleCheckin() {
    if (!selectedPFile) return;

    setCheckingIn(true);
    try {
      const response = await institutionPFiles.checkin(
        selectedPFile.id,
        checkinNotes || undefined,
        checkinLocation || undefined
      );

      if (response.success) {
        setShowCheckinModal(false);
        setCheckinNotes('');
        setCheckinLocation('');
        fetchPFiles();
        if (response.data) {
          setSelectedPFile(response.data);
        }
      }
    } catch (err) {
      console.error('Checkin failed:', err);
    } finally {
      setCheckingIn(false);
    }
  }

  // Transfer
  async function handleTransfer() {
    if (!selectedPFile || !transferToUserId) return;

    setTransferring(true);
    try {
      const response = await institutionPFiles.transfer(
        selectedPFile.id,
        transferToUserId,
        transferNotes || undefined
      );

      if (response.success) {
        setShowTransferModal(false);
        setTransferToUserId('');
        setTransferNotes('');
        fetchPFiles();
        if (response.data) {
          setSelectedPFile(response.data);
        }
      }
    } catch (err) {
      console.error('Transfer failed:', err);
    } finally {
      setTransferring(false);
    }
  }

  // Request File
  async function handleRequest() {
    if (!selectedPFile || !requestPurpose) return;

    setRequesting(true);
    try {
      const response = await institutionPFiles.requestFile(
        selectedPFile.id,
        requestPurpose,
        requestUrgency,
        requestNeededBy || undefined
      );

      if (response.success) {
        setShowRequestModal(false);
        setRequestPurpose('');
        setRequestUrgency('normal');
        setRequestNeededBy('');
        fetchPendingRequests();
      }
    } catch (err) {
      console.error('Request failed:', err);
    } finally {
      setRequesting(false);
    }
  }

  // Review request
  async function handleReviewRequest(requestId: string, action: 'approve' | 'deny') {
    try {
      const response = await institutionPFiles.reviewRequest(requestId, action);
      if (response.success) {
        fetchPendingRequests();
        fetchReferenceData();
      }
    } catch (err) {
      console.error('Review failed:', err);
    }
  }

  function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }

  function formatDateTime(dateStr: string): string {
    return new Date(dateStr).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  function getStatusColor(status: string): string {
    switch (status) {
      case 'active': return 'var(--color-success)';
      case 'inactive': return 'var(--text-muted)';
      case 'archived': return 'var(--color-info)';
      case 'transferred': return 'var(--color-warning)';
      case 'destroyed': return 'var(--color-error)';
      default: return 'var(--text-muted)';
    }
  }

  function getMovementIcon(type: string): string {
    switch (type) {
      case 'created': return '+';
      case 'checked_out': return '↗';
      case 'checked_in': return '↙';
      case 'transferred': return '→';
      case 'location_changed': return '📍';
      case 'custody_changed': return '👤';
      case 'status_changed': return '●';
      case 'requested': return '?';
      case 'request_approved': return '✓';
      case 'request_denied': return '✗';
      default: return '•';
    }
  }

  // Get staff members who don't have a PFile yet
  const staffWithoutPFile = staffMembers.filter(
    staff => !pfiles.some(pf => pf.staffId === staff.id)
  );

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>Personnel File Jackets</h1>
          <p className={styles.subtitle}>Manage physical and digital staff file jackets</p>
        </div>
        <div className={styles.headerActions}>
          {flaggedPFiles.length > 0 && (
            <Button variant="secondary" onClick={() => setShowAnomalyPanel(true)} className={styles.alertBtn}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <span className={styles.alertBadge}>{flaggedPFiles.length}</span>
              Records Need Attention
            </Button>
          )}
          {pendingRequests.length > 0 && (
            <Button variant="secondary" onClick={() => setShowRequestsModal(true)}>
              <span className={styles.badge}>{pendingRequests.length}</span>
              Pending Requests
            </Button>
          )}
          <Button variant="secondary" onClick={() => setShowDocScanner(true)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
            Scan Document
          </Button>
          <Button onClick={() => setShowCreateModal(true)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
            New File Jacket
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className={styles.statsBar}>
          <div className={styles.stat}>
            <span className={styles.statValue}>{stats.total}</span>
            <span className={styles.statLabel}>Total Files</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statValue}>{stats.active}</span>
            <span className={styles.statLabel}>Active</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statValue} style={{ color: stats.checkedOut > 0 ? 'var(--color-warning)' : undefined }}>
              {stats.checkedOut}
            </span>
            <span className={styles.statLabel}>Checked Out</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statValue}>{stats.archived}</span>
            <span className={styles.statLabel}>Archived</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statValue} style={{ color: stats.pendingRequests > 0 ? 'var(--color-info)' : undefined }}>
              {stats.pendingRequests}
            </span>
            <span className={styles.statLabel}>Pending Requests</span>
          </div>
        </div>
      )}

      {/* Smart Search Toggle */}
      <div className={styles.searchToggle}>
        <button
          className={`${styles.searchModeBtn} ${!showSmartSearch ? styles.active : ''}`}
          onClick={() => setShowSmartSearch(false)}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          Basic Search
        </button>
        <button
          className={`${styles.searchModeBtn} ${showSmartSearch ? styles.active : ''}`}
          onClick={() => setShowSmartSearch(true)}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z" />
            <circle cx="7.5" cy="14.5" r="1.5" />
            <circle cx="16.5" cy="14.5" r="1.5" />
          </svg>
          AI Smart Search
        </button>
      </div>

      {/* Filters */}
      <div className={styles.filters}>
        {showSmartSearch ? (
          <SmartSearch
            onSearch={handleSmartSearch}
            placeholder="Try: 'Show all files checked out for more than 30 days' or 'Find Grade 14 officers retiring in 2025'"
            className={styles.smartSearchBox}
          />
        ) : (
          <div className={styles.searchBox}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder="Search by file number, staff name, or employee ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
          </div>
        )}
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className={styles.filterSelect}
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="archived">Archived</option>
          <option value="transferred">Transferred</option>
        </select>
        <select
          value={filterCheckedOut}
          onChange={(e) => setFilterCheckedOut(e.target.value)}
          className={styles.filterSelect}
        >
          <option value="">All Files</option>
          <option value="true">Checked Out Only</option>
          <option value="false">Available Only</option>
        </select>
      </div>

      {/* Table */}
      <div className={styles.tableContainer}>
        {loading ? (
          <div className={styles.loading}>Loading file jackets...</div>
        ) : pfiles.length === 0 ? (
          <div className={styles.emptyState}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            </svg>
            <p>No file jackets found</p>
            <Button variant="secondary" onClick={() => setShowCreateModal(true)}>Create First File Jacket</Button>
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>File Number</th>
                <th>Staff Member</th>
                <th>Location</th>
                <th>Status</th>
                <th>Documents</th>
                <th>Custodian</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pfiles.map((pfile) => {
                const anomalyReport = anomalyReports.get(pfile.id);
                const hasAnomalies = anomalyReport && anomalyReport.anomalies.length > 0;
                const riskClass = anomalyReport?.riskLevel === 'critical' ? styles.criticalRisk :
                                  anomalyReport?.riskLevel === 'high' ? styles.highRisk :
                                  anomalyReport?.riskLevel === 'medium' ? styles.mediumRisk : '';
                return (
                <tr key={pfile.id} className={`${pfile.isCheckedOut ? styles.checkedOutRow : ''} ${riskClass}`}>
                  <td>
                    <div className={styles.fileNumberCell}>
                      <button className={styles.fileNumberBtn} onClick={() => openDetailSidebar(pfile)}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                        </svg>
                        <span>{pfile.fileNumber}</span>
                      </button>
                      {hasAnomalies && (
                        <button
                          className={`${styles.anomalyIndicator} ${styles[anomalyReport.riskLevel]}`}
                          onClick={() => setSelectedAnomalyReport(anomalyReport)}
                          title={`${anomalyReport.anomalies.length} issue${anomalyReport.anomalies.length > 1 ? 's' : ''} detected`}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                            <line x1="12" y1="9" x2="12" y2="13" />
                            <line x1="12" y1="17" x2="12.01" y2="17" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className={styles.staffCell}>
                      <div className={styles.staffAvatar}>
                        {pfile.staff.firstName[0]}{pfile.staff.lastName[0]}
                      </div>
                      <div className={styles.staffInfo}>
                        <span className={styles.staffName}>
                          {pfile.staff.firstName} {pfile.staff.lastName}
                        </span>
                        {pfile.staff.employeeId && (
                          <span className={styles.staffId}>{pfile.staff.employeeId}</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={styles.location}>
                      {pfile.physicalLocation || [pfile.building, pfile.room, pfile.cabinet, pfile.shelf].filter(Boolean).join(' / ') || 'Not specified'}
                    </span>
                  </td>
                  <td>
                    <div className={styles.statusCell}>
                      <span
                        className={styles.statusBadge}
                        style={{ backgroundColor: `${getStatusColor(pfile.status)}20`, color: getStatusColor(pfile.status) }}
                      >
                        {pfile.status}
                      </span>
                      {pfile.isCheckedOut && (
                        <span className={styles.checkedOutBadge}>
                          Checked Out
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={styles.docCount}>{pfile.documentCount}</span>
                  </td>
                  <td>
                    {pfile.isCheckedOut && pfile.checkedOutToUser ? (
                      <span className={styles.custodian}>
                        {pfile.checkedOutToUser.firstName} {pfile.checkedOutToUser.lastName}
                      </span>
                    ) : pfile.custodian ? (
                      <span className={styles.custodian}>
                        {pfile.custodian.firstName} {pfile.custodian.lastName}
                      </span>
                    ) : (
                      <span className={styles.noCustodian}>Records Office</span>
                    )}
                  </td>
                  <td>
                    <div className={styles.actions}>
                      <button
                        className={styles.actionBtn}
                        title="View Details"
                        onClick={() => openDetailSidebar(pfile)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      </button>
                      {!pfile.isCheckedOut ? (
                        <button
                          className={styles.actionBtn}
                          title="Checkout"
                          onClick={() => { setSelectedPFile(pfile); setShowCheckoutModal(true); }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M7 17l9.2-9.2M17 17V7H7" />
                          </svg>
                        </button>
                      ) : (
                        <button
                          className={styles.actionBtn}
                          title="Check In"
                          onClick={() => { setSelectedPFile(pfile); setShowCheckinModal(true); }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M17 7l-9.2 9.2M7 7v10h10" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail Sidebar */}
      {showDetailSidebar && selectedPFile && (
        <div className={styles.sidebarOverlay} onClick={closeDetailSidebar}>
          <div className={styles.sidebar} onClick={(e) => e.stopPropagation()}>
            <div className={styles.sidebarHeader}>
              <div className={styles.sidebarHeaderContent}>
                <div className={styles.fileIcon}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className={styles.sidebarTitle}>{selectedPFile.fileNumber}</h2>
                  <p className={styles.sidebarSubtitle}>Personnel File Jacket</p>
                </div>
              </div>
              <button className={styles.closeBtn} onClick={closeDetailSidebar}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className={styles.sidebarContent}>
              {/* Staff Info */}
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Staff Member</h3>
                <div className={styles.staffCard}>
                  <div className={styles.staffAvatarLarge}>
                    {selectedPFile.staff.firstName[0]}{selectedPFile.staff.lastName[0]}
                  </div>
                  <div className={styles.staffDetails}>
                    <span className={styles.staffNameLarge}>
                      {selectedPFile.staff.firstName} {selectedPFile.staff.lastName}
                    </span>
                    <span className={styles.staffEmail}>{selectedPFile.staff.email}</span>
                    {selectedPFile.staff.employeeId && (
                      <span className={styles.staffIdLarge}>ID: {selectedPFile.staff.employeeId}</span>
                    )}
                    {selectedPFile.staff.department && (
                      <span className={styles.staffDept}>{selectedPFile.staff.department.name}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* File Details */}
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>File Details</h3>
                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Status</span>
                    <span
                      className={styles.statusBadge}
                      style={{ backgroundColor: `${getStatusColor(selectedPFile.status)}20`, color: getStatusColor(selectedPFile.status) }}
                    >
                      {selectedPFile.status}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Documents</span>
                    <span className={styles.detailValue}>{selectedPFile.documentCount} records</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Created</span>
                    <span className={styles.detailValue}>{formatDate(selectedPFile.createdAt)}</span>
                  </div>
                  {selectedPFile.retentionPeriodYears && (
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Retention</span>
                      <span className={styles.detailValue}>{selectedPFile.retentionPeriodYears} years</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Retention & Compliance */}
              {retentionSchedule && (
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>Retention & Compliance</h3>
                  <div className={styles.retentionCard}>
                    <div className={styles.retentionStatus}>
                      <span
                        className={styles.retentionBadge}
                        style={{
                          backgroundColor: retentionSchedule.status === 'active' ? 'rgba(0, 107, 63, 0.1)' :
                                           retentionSchedule.status === 'legal_hold' ? 'rgba(239, 68, 68, 0.1)' :
                                           retentionSchedule.status === 'pending_disposition' ? 'rgba(245, 158, 11, 0.1)' :
                                           'rgba(107, 114, 128, 0.1)',
                          color: retentionSchedule.status === 'active' ? 'var(--ghana-green)' :
                                 retentionSchedule.status === 'legal_hold' ? '#ef4444' :
                                 retentionSchedule.status === 'pending_disposition' ? '#f59e0b' : '#6b7280'
                        }}
                      >
                        {retentionSchedule.status.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <div className={styles.retentionDetails}>
                      <div className={styles.retentionItem}>
                        <span className={styles.retentionLabel}>Policy</span>
                        <span className={styles.retentionValue}>{retentionSchedule.policyName}</span>
                      </div>
                      {retentionSchedule.retentionEndDate && (
                        <div className={styles.retentionItem}>
                          <span className={styles.retentionLabel}>Retention Ends</span>
                          <span className={styles.retentionValue}>
                            {formatDate(retentionSchedule.retentionEndDate.toISOString())}
                          </span>
                        </div>
                      )}
                      <div className={styles.retentionItem}>
                        <span className={styles.retentionLabel}>Disposition Action</span>
                        <span className={styles.retentionValue}>
                          {retentionSchedule.dispositionAction.replace(/_/g, ' ')}
                        </span>
                      </div>
                      {retentionSchedule.nextReviewDate && (
                        <div className={styles.retentionItem}>
                          <span className={styles.retentionLabel}>Next Review</span>
                          <span className={styles.retentionValue}>
                            {formatDate(retentionSchedule.nextReviewDate.toISOString())}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Location */}
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Physical Location</h3>
                <div className={styles.locationCard}>
                  {selectedPFile.physicalLocation || selectedPFile.building || selectedPFile.room || selectedPFile.cabinet ? (
                    <>
                      {selectedPFile.building && <div><strong>Directorate/Unit:</strong> {selectedPFile.building}</div>}
                      {selectedPFile.room && <div><strong>Room:</strong> {selectedPFile.room}</div>}
                      {selectedPFile.cabinet && <div><strong>Cabinet:</strong> {selectedPFile.cabinet}</div>}
                      {selectedPFile.shelf && <div><strong>Shelf:</strong> {selectedPFile.shelf}</div>}
                      {selectedPFile.box && <div><strong>Box:</strong> {selectedPFile.box}</div>}
                      {selectedPFile.physicalLocation && !selectedPFile.building && (
                        <div>{selectedPFile.physicalLocation}</div>
                      )}
                    </>
                  ) : (
                    <span className={styles.noLocation}>Location not specified</span>
                  )}
                </div>
              </div>

              {/* Custody */}
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Current Custody</h3>
                {selectedPFile.isCheckedOut ? (
                  <div className={styles.custodyCard} style={{ borderColor: 'var(--color-warning)' }}>
                    <div className={styles.custodyStatus}>Checked Out</div>
                    {selectedPFile.checkedOutToUser && (
                      <div className={styles.custodyUser}>
                        To: {selectedPFile.checkedOutToUser.firstName} {selectedPFile.checkedOutToUser.lastName}
                      </div>
                    )}
                    {selectedPFile.checkedOutAt && (
                      <div className={styles.custodyDate}>Since: {formatDateTime(selectedPFile.checkedOutAt)}</div>
                    )}
                    {selectedPFile.checkoutReason && (
                      <div className={styles.custodyReason}>Reason: {selectedPFile.checkoutReason}</div>
                    )}
                    {selectedPFile.expectedReturnDate && (
                      <div className={styles.custodyReturn}>Expected Return: {formatDate(selectedPFile.expectedReturnDate)}</div>
                    )}
                  </div>
                ) : selectedPFile.custodian ? (
                  <div className={styles.custodyCard}>
                    <div className={styles.custodyUser}>
                      {selectedPFile.custodian.firstName} {selectedPFile.custodian.lastName}
                    </div>
                    {selectedPFile.custodianSince && (
                      <div className={styles.custodyDate}>Since: {formatDate(selectedPFile.custodianSince)}</div>
                    )}
                  </div>
                ) : (
                  <div className={styles.custodyCard}>
                    <div className={styles.custodyUser}>Records Office</div>
                  </div>
                )}
              </div>

              {/* Movement History */}
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Movement History</h3>
                {loadingMovements ? (
                  <div className={styles.loading}>Loading...</div>
                ) : movements.length === 0 ? (
                  <div className={styles.noMovements}>No movements recorded</div>
                ) : (
                  <div className={styles.movementsList}>
                    {movements.slice(0, 10).map((movement) => (
                      <div key={movement.id} className={styles.movementItem}>
                        <span className={styles.movementIcon}>{getMovementIcon(movement.movementType)}</span>
                        <div className={styles.movementContent}>
                          <span className={styles.movementType}>
                            {movement.movementType.replace(/_/g, ' ')}
                          </span>
                          {movement.notes && (
                            <span className={styles.movementNotes}>{movement.notes}</span>
                          )}
                          <span className={styles.movementDate}>{formatDateTime(movement.performedAt)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Notes */}
              {selectedPFile.notes && (
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>Notes</h3>
                  <p className={styles.notes}>{selectedPFile.notes}</p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className={styles.sidebarActions}>
              {!selectedPFile.isCheckedOut ? (
                <>
                  <Button onClick={() => setShowCheckoutModal(true)}>
                    Checkout File
                  </Button>
                  <Button variant="secondary" onClick={() => setShowTransferModal(true)}>
                    Transfer Custody
                  </Button>
                  <Button variant="secondary" onClick={() => setShowRequestModal(true)}>
                    Request File
                  </Button>
                </>
              ) : (
                <Button onClick={() => setShowCheckinModal(true)}>
                  Check In File
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New File Jacket"
      >
        <form onSubmit={handleCreatePFile}>
          {createError && <div className={styles.error}>{createError}</div>}

          <Select
            label="Staff Member"
            value={createForm.staffId}
            onChange={(e) => handleStaffChange(e.target.value)}
            required
          >
            <option value="">Select Staff Member</option>
            {staffWithoutPFile.map((staff) => (
              <option key={staff.id} value={staff.id}>
                {staff.firstName} {staff.lastName} {staff.employeeId ? `(${staff.employeeId})` : ''} - {staff.department?.name || 'No Dept'}
              </option>
            ))}
          </Select>

          {/* Document Linking Section */}
          {createForm.staffId && (
            <div className={styles.formSection}>
              <h4 className={styles.formSectionTitle}>
                Link Existing Documents
                {selectedDocIds.length > 0 && (
                  <span className={styles.selectedCount}> ({selectedDocIds.length} selected)</span>
                )}
              </h4>
              {loadingDocs ? (
                <div className={styles.loadingDocs}>Loading documents...</div>
              ) : unassignedDocs.length === 0 ? (
                <div className={styles.noDocsMessage}>
                  No unassigned documents found for this staff member.
                </div>
              ) : (
                <div className={styles.docSelectList}>
                  {unassignedDocs.map((doc) => (
                    <label key={doc.id} className={styles.docSelectItem}>
                      <input
                        type="checkbox"
                        checked={selectedDocIds.includes(doc.id)}
                        onChange={() => toggleDocSelection(doc.id)}
                      />
                      <div className={styles.docSelectInfo}>
                        <span className={styles.docSelectTitle}>{doc.title}</span>
                        <span className={styles.docSelectMeta}>
                          {doc.docTypeName || 'Document'} • {new Date(doc.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className={styles.formSection}>
            <h4 className={styles.formSectionTitle}>Physical Location (Optional)</h4>
            <div className={styles.formGrid}>
              <Input
                label="Directorate/Unit"
                value={createForm.building}
                onChange={(e) => setCreateForm({ ...createForm, building: e.target.value })}
              />
              <Input
                label="Room"
                value={createForm.room}
                onChange={(e) => setCreateForm({ ...createForm, room: e.target.value })}
              />
              <Input
                label="Cabinet"
                value={createForm.cabinet}
                onChange={(e) => setCreateForm({ ...createForm, cabinet: e.target.value })}
              />
              <Input
                label="Shelf"
                value={createForm.shelf}
                onChange={(e) => setCreateForm({ ...createForm, shelf: e.target.value })}
              />
            </div>
          </div>

          <Input
            label="Notes"
            value={createForm.notes}
            onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
          />

          <div className={styles.modalActions}>
            <Button type="button" variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={creating}>
              {creating ? 'Creating...' : 'Create File Jacket'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Checkout Modal */}
      <Modal
        isOpen={showCheckoutModal}
        onClose={() => setShowCheckoutModal(false)}
        title="Checkout File"
      >
        <p className={styles.modalDescription}>
          You are checking out file <strong>{selectedPFile?.fileNumber}</strong>.
        </p>
        <Input
          label="Reason for Checkout"
          value={checkoutReason}
          onChange={(e) => setCheckoutReason(e.target.value)}
          required
          hint="e.g., Performance review, Promotion board review"
        />
        <Input
          label="Expected Return Date"
          type="date"
          value={expectedReturnDate}
          onChange={(e) => setExpectedReturnDate(e.target.value)}
          hint="Optional"
        />
        <div className={styles.modalActions}>
          <Button type="button" variant="secondary" onClick={() => setShowCheckoutModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleCheckout} disabled={!checkoutReason || checkingOut}>
            {checkingOut ? 'Checking Out...' : 'Checkout'}
          </Button>
        </div>
      </Modal>

      {/* Checkin Modal */}
      <Modal
        isOpen={showCheckinModal}
        onClose={() => setShowCheckinModal(false)}
        title="Check In File"
      >
        <p className={styles.modalDescription}>
          You are returning file <strong>{selectedPFile?.fileNumber}</strong>.
        </p>
        <Input
          label="Notes"
          value={checkinNotes}
          onChange={(e) => setCheckinNotes(e.target.value)}
          hint="Optional notes about the return"
        />
        <Input
          label="New Location"
          value={checkinLocation}
          onChange={(e) => setCheckinLocation(e.target.value)}
          hint="Leave empty to use previous location"
        />
        <div className={styles.modalActions}>
          <Button type="button" variant="secondary" onClick={() => setShowCheckinModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleCheckin} disabled={checkingIn}>
            {checkingIn ? 'Checking In...' : 'Check In'}
          </Button>
        </div>
      </Modal>

      {/* Transfer Modal */}
      <Modal
        isOpen={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        title="Transfer Custody"
      >
        <p className={styles.modalDescription}>
          Transfer custody of file <strong>{selectedPFile?.fileNumber}</strong> to another user.
        </p>
        <Select
          label="Transfer To"
          value={transferToUserId}
          onChange={(e) => setTransferToUserId(e.target.value)}
          required
        >
          <option value="">Select User</option>
          {staffMembers.filter(s => s.id !== selectedPFile?.currentCustodianId).map((staff) => (
            <option key={staff.id} value={staff.id}>
              {staff.firstName} {staff.lastName} - {staff.department?.name || 'No Dept'}
            </option>
          ))}
        </Select>
        <Input
          label="Notes"
          value={transferNotes}
          onChange={(e) => setTransferNotes(e.target.value)}
          hint="Optional transfer notes"
        />
        <div className={styles.modalActions}>
          <Button type="button" variant="secondary" onClick={() => setShowTransferModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleTransfer} disabled={!transferToUserId || transferring}>
            {transferring ? 'Transferring...' : 'Transfer'}
          </Button>
        </div>
      </Modal>

      {/* Request Modal */}
      <Modal
        isOpen={showRequestModal}
        onClose={() => setShowRequestModal(false)}
        title="Request File"
      >
        <p className={styles.modalDescription}>
          Request access to file <strong>{selectedPFile?.fileNumber}</strong>.
        </p>
        <Input
          label="Purpose"
          value={requestPurpose}
          onChange={(e) => setRequestPurpose(e.target.value)}
          required
          hint="Why do you need this file?"
        />
        <Select
          label="Urgency"
          value={requestUrgency}
          onChange={(e) => setRequestUrgency(e.target.value)}
        >
          <option value="low">Low</option>
          <option value="normal">Normal</option>
          <option value="high">High</option>
          <option value="urgent">Urgent</option>
        </Select>
        <Input
          label="Needed By"
          type="date"
          value={requestNeededBy}
          onChange={(e) => setRequestNeededBy(e.target.value)}
          hint="Optional deadline"
        />
        <div className={styles.modalActions}>
          <Button type="button" variant="secondary" onClick={() => setShowRequestModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleRequest} disabled={!requestPurpose || requesting}>
            {requesting ? 'Submitting...' : 'Submit Request'}
          </Button>
        </div>
      </Modal>

      {/* Pending Requests Modal */}
      <Modal
        isOpen={showRequestsModal}
        onClose={() => setShowRequestsModal(false)}
        title="Pending File Requests"
      >
        {pendingRequests.length === 0 ? (
          <p>No pending requests</p>
        ) : (
          <div className={styles.requestsList}>
            {pendingRequests.map((request) => (
              <div key={request.id} className={styles.requestCard}>
                <div className={styles.requestHeader}>
                  <span className={styles.requestFile}>{request.pfile.fileNumber}</span>
                  <span className={`${styles.urgencyBadge} ${styles[request.urgency]}`}>
                    {request.urgency}
                  </span>
                </div>
                <div className={styles.requestDetails}>
                  <div><strong>Staff:</strong> {request.pfile.staff.firstName} {request.pfile.staff.lastName}</div>
                  <div><strong>Requested By:</strong> {request.requester.firstName} {request.requester.lastName}</div>
                  <div><strong>Purpose:</strong> {request.purpose}</div>
                  {request.neededBy && <div><strong>Needed By:</strong> {formatDate(request.neededBy)}</div>}
                  <div><strong>Submitted:</strong> {formatDateTime(request.requestedAt)}</div>
                </div>
                <div className={styles.requestActions}>
                  <Button size="sm" onClick={() => handleReviewRequest(request.id, 'approve')}>
                    Approve
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => handleReviewRequest(request.id, 'deny')}>
                    Deny
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Document Scanner Modal */}
      <Modal
        isOpen={showDocScanner}
        onClose={() => setShowDocScanner(false)}
        title="Smart Document Scanner"
      >
        <div className={styles.scannerModal}>
          <p className={styles.scannerDescription}>
            Scan a document to automatically extract staff information and metadata using AI-powered OCR.
          </p>
          <DocumentScanner
            onScanComplete={(result) => {
              console.log('OCR Result:', result);
            }}
            onExtractedData={(data) => {
              console.log('Extracted Data:', data);
              // Could auto-fill a form or search for matching records
              if (data.staffName) {
                setSearchQuery(data.staffName);
              }
              setShowDocScanner(false);
            }}
          />
        </div>
      </Modal>

      {/* Anomaly Report Modal */}
      <Modal
        isOpen={!!selectedAnomalyReport}
        onClose={() => setSelectedAnomalyReport(null)}
        title="Record Health Report"
      >
        {selectedAnomalyReport && (
          <div className={styles.anomalyModal}>
            <div className={styles.anomalyHeader}>
              <div className={styles.anomalyStaff}>{selectedAnomalyReport.staffName}</div>
              <div className={`${styles.riskBadge} ${styles[selectedAnomalyReport.riskLevel]}`}>
                {selectedAnomalyReport.riskLevel} Risk
              </div>
            </div>

            <div className={styles.completenessSection}>
              <div className={styles.completenessLabel}>Completeness Score</div>
              <div className={styles.completenessBar}>
                <div
                  className={styles.completenessProgress}
                  style={{
                    width: `${selectedAnomalyReport.completenessScore}%`,
                    backgroundColor: selectedAnomalyReport.completenessScore >= 80 ? 'var(--color-success)' :
                                     selectedAnomalyReport.completenessScore >= 50 ? 'var(--color-warning)' : 'var(--color-error)'
                  }}
                />
              </div>
              <div className={styles.completenessValue}>{selectedAnomalyReport.completenessScore}%</div>
            </div>

            {selectedAnomalyReport.anomalies.length > 0 && (
              <div className={styles.anomaliesSection}>
                <h4>Issues Detected ({selectedAnomalyReport.anomalies.length})</h4>
                <div className={styles.anomaliesList}>
                  {selectedAnomalyReport.anomalies.map((anomaly, index) => (
                    <div key={index} className={`${styles.anomalyItem} ${styles[anomaly.severity]}`}>
                      <div className={styles.anomalyType}>{anomaly.type.replace(/_/g, ' ')}</div>
                      <div className={styles.anomalyMessage}>{anomaly.message}</div>
                      {anomaly.suggestedAction && (
                        <div className={styles.anomalyRecommendation}>
                          <strong>Recommendation:</strong> {anomaly.suggestedAction}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedAnomalyReport.recommendations.length > 0 && (
              <div className={styles.recommendationsSection}>
                <h4>Recommendations</h4>
                <ul className={styles.recommendationsList}>
                  {selectedAnomalyReport.recommendations.map((rec, index) => (
                    <li key={index}>{rec}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className={styles.modalActions}>
              <Button variant="secondary" onClick={() => setSelectedAnomalyReport(null)}>
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Anomaly Panel Modal - Shows all flagged records */}
      <Modal
        isOpen={showAnomalyPanel}
        onClose={() => setShowAnomalyPanel(false)}
        title="Records Needing Attention"
      >
        <div className={styles.anomalyPanel}>
          <p className={styles.anomalyPanelDescription}>
            The following records have been flagged by the AI system for potential issues.
          </p>
          <div className={styles.flaggedList}>
            {flaggedPFiles.map(pfile => {
              const report = anomalyReports.get(pfile.id);
              if (!report) return null;
              return (
                <div
                  key={pfile.id}
                  className={`${styles.flaggedItem} ${styles[report.riskLevel]}`}
                  onClick={() => {
                    setSelectedAnomalyReport(report);
                    setShowAnomalyPanel(false);
                  }}
                >
                  <div className={styles.flaggedHeader}>
                    <span className={styles.flaggedFileNumber}>{pfile.fileNumber}</span>
                    <span className={`${styles.riskBadge} ${styles[report.riskLevel]}`}>
                      {report.riskLevel}
                    </span>
                  </div>
                  <div className={styles.flaggedStaff}>
                    {pfile.staff.firstName} {pfile.staff.lastName}
                  </div>
                  <div className={styles.flaggedStats}>
                    <span>{report.anomalies.length} issues</span>
                    <span>{report.completenessScore}% complete</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Modal>
    </div>
  );
}
